import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Loader2, RefreshCw, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useTenantPath } from '@/hooks/useTenantPath';
import { getApiErrorMessage } from '@/lib/api/errors';
import {
  fetchPayrollOwnerSummary,
  isPayrollMfaError,
  sendPayrollPrepDigest,
  type PayrollOwnerAlert,
  type PayrollOwnerSummary,
} from '@/lib/api/payrollBridge';
import { PayrollMfaGate } from './PayrollMfaGate';

const currency = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

const percent = (value: number): string => `${value.toFixed(1)}%`;

const riskBadgeStyles: Record<'low' | 'medium' | 'high', string> = {
  low: 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
  medium:
    'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  high: 'border-rose-200 bg-rose-100 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100',
};

const alertSeverityStyles: Record<'info' | 'warning' | 'critical', string> = {
  info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  critical:
    'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100',
};

const metricCardClass =
  'rounded-xl border border-border bg-card shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60';

const OWNER_SUMMARY_QUERY_KEY = ['payroll-owner-summary'];

interface HighlightItem {
  id: string;
  label: string;
  value: string;
  subLabel?: string | null;
  badge?: string | null;
  badgeClassName?: string;
}

const buildHighlights = (
  summary: PayrollOwnerSummary,
  t: ReturnType<typeof useTranslation>['t'],
): HighlightItem[] => {
  const highlights: HighlightItem[] = [
    {
      id: 'labor',
      label: t('payrollBridge:owner.laborPercent'),
      value: percent(summary.metrics.laborPercent),
      subLabel:
        summary.metrics.laborPercentDelta !== null
          ? `${t('payrollBridge:owner.laborDelta')}: ${summary.metrics.laborPercentDelta.toFixed(1)}`
          : null,
    },
    {
      id: 'labor-cost',
      label: t('payrollBridge:owner.laborSpend', { defaultValue: 'Estimated labor spend' }),
      value: currency.format(summary.metrics.estimatedLaborCostCents / 100),
      subLabel: `${t('payrollBridge:owner.salesValue', { defaultValue: 'Sales' })}: ${currency.format(
        summary.metrics.salesCents / 100,
      )}`,
    },
    {
      id: 'overtime',
      label: t('payrollBridge:owner.overtimeShare'),
      value: percent(summary.metrics.overtimeShare * 100),
      subLabel: t('payrollBridge:owner.hoursValue', {
        hours: summary.metrics.overtimeHours.toFixed(2),
      }),
    },
    {
      id: 'tips',
      label: t('payrollBridge:owner.tipsCaptured', { defaultValue: 'Tips captured' }),
      value: currency.format(summary.metrics.totalTipsCents / 100),
      subLabel: `${t('payrollBridge:owner.tipsPerHour', { defaultValue: 'Tips per hour' })}: ${currency.format(
        summary.metrics.averageTipsPerHourCents / 100,
      )}`,
    },
  ];

  const riskLevel = summary.metrics.tipCreditRiskLevel;
  highlights.push({
    id: 'tip-risk',
    label: t('payrollBridge:owner.tipCreditRisk'),
    value: t(`payrollBridge:owner.risk${riskLevel.charAt(0).toUpperCase()}${riskLevel.slice(1)}`),
    badge: t('payrollBridge:owner.tipCreditBadge', {
      count: summary.metrics.tipCreditFlaggedEmployees,
    }),
    badgeClassName: riskBadgeStyles[riskLevel],
  });

  highlights.push({
    id: 'onboarding',
    label: t('payrollBridge:owner.pendingOnboarding'),
    value: String(summary.onboarding.pendingEmployees),
    subLabel: `${summary.onboarding.completionRate.toFixed(1)}%`,
  });

  highlights.push({
    id: 'exceptions',
    label: t('payrollBridge:owner.openExceptions'),
    value: String(summary.exceptions.openCount),
  });

  const exportLabel =
    summary.exports.latestStatus && summary.exports.latestRunAt
      ? `${summary.exports.latestStatus} · ${new Date(summary.exports.latestRunAt).toLocaleString()}`
      : t('payrollBridge:owner.neverExported');

  highlights.push({
    id: 'export',
    label: t('payrollBridge:owner.lastExport'),
    value: exportLabel,
  });

  return highlights;
};

const AlertsList = ({
  alerts,
  emptyText,
  reviewLabel,
  resolveHref,
}: {
  alerts: PayrollOwnerAlert[];
  emptyText: string;
  reviewLabel: string;
  resolveHref?: (href: string) => string | null;
}) => {
  if (alerts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground dark:border-border/50 dark:bg-muted/10">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => {
        const href =
          alert.ctaHref && resolveHref ? resolveHref(alert.ctaHref) : alert.ctaHref ?? null;

        return (
          <div
            key={alert.id}
            className={cn(
              'rounded-lg border px-4 py-3 shadow-sm',
              alertSeverityStyles[alert.severity],
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{alert.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{alert.message}</p>
              </div>
              {href ? (
                <Button asChild variant="outline" size="sm">
                  <a href={href}>{alert.ctaLabel ?? reviewLabel}</a>
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const PayrollOwnerDashboard = () => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [range, setRange] = useState<'last_7_days' | 'last_30_days'>('last_7_days');
  const { tenantPath, slug: tenantSlug } = useTenantPath();

  const summaryQuery = useQuery({
    queryKey: [...OWNER_SUMMARY_QUERY_KEY, range],
    queryFn: () => fetchPayrollOwnerSummary(range),
    enabled: !mfaRequired,
    retry: (failureCount, error) => {
      if (isPayrollMfaError(error)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (!mfaRequired && summaryQuery.error && isPayrollMfaError(summaryQuery.error)) {
      setMfaRequired(true);
    }
  }, [mfaRequired, summaryQuery.error]);

  const handleMfaVerified = useCallback(() => {
    setMfaRequired(false);
    queryClient
      .resetQueries({ queryKey: OWNER_SUMMARY_QUERY_KEY })
      .catch(() => undefined);
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: OWNER_SUMMARY_QUERY_KEY }).catch(() => undefined);
    }, 0);
  }, [queryClient]);

  const digestMutation = useMutation({
    mutationFn: sendPayrollPrepDigest,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: OWNER_SUMMARY_QUERY_KEY }).catch(() => undefined);
      const count = result.result.recipients;
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description:
          count === 1
            ? t('payrollBridge:owner.digestSuccess', { count })
            : t('payrollBridge:owner.digestSuccessPlural', { count }),
      });
    },
    onError: (error: unknown) => {
      if (isPayrollMfaError(error)) {
        setMfaRequired(true);
      }

      const errorMessage =
        isPayrollMfaError(error)
          ? t('payrollBridge:mfa.required', { defaultValue: 'Payroll MFA verification required.' })
          : getApiErrorMessage(error) ??
            (error instanceof Error
              ? error.message
              : t('common:status.tryAgain', { defaultValue: 'Please try again.' }));

      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: t('payrollBridge:owner.digestError', { message: errorMessage }),
      });
    },
  });

  const resolveAlertHref = useCallback(
    (rawHref: string): string | null => {
      if (!rawHref) {
        return null;
      }

      let href = rawHref;
      if (href.includes(':subdomain')) {
        if (!tenantSlug) {
          return null;
        }
        href = href.replace(/:subdomain/g, tenantSlug);
      }

      if (/^(https?:)?\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return href;
      }
      if (href.startsWith('#')) {
        return href;
      }
      if (href.startsWith('/r/')) {
        return href;
      }
      const trimmed = href.startsWith('/') ? href.slice(1) : href;
      return tenantPath(trimmed);
    },
    [tenantPath, tenantSlug],
  );

  const highlights = useMemo(() => {
    if (!summaryQuery.data) {
      return [];
    }
    return buildHighlights(summaryQuery.data, t);
  }, [summaryQuery.data, t]);

  const requiresMfa = mfaRequired || (summaryQuery.error && isPayrollMfaError(summaryQuery.error));

  const queryError = summaryQuery.error;
  const showQueryError = Boolean(queryError) && !requiresMfa;
  const queryErrorMessage = showQueryError
    ? getApiErrorMessage(queryError) ?? (queryError instanceof Error ? queryError.message : null)
    : null;

  const isLoading = summaryQuery.fetchStatus === 'fetching' && !requiresMfa;
  const isRefetching = summaryQuery.isRefetching && !requiresMfa;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t('payrollBridge:owner.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('payrollBridge:owner.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={range}
            onValueChange={(value: 'last_7_days' | 'last_30_days') => setRange(value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days">
                {t('payrollBridge:owner.range.last7Days', { defaultValue: 'Last 7 days' })}
              </SelectItem>
              <SelectItem value="last_30_days">
                {t('payrollBridge:owner.range.last30Days', { defaultValue: 'Last 30 days' })}
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => summaryQuery.refetch()}
            disabled={summaryQuery.isFetching || requiresMfa}
          >
            {summaryQuery.isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {t('payrollBridge:owner.refresh')}
          </Button>
          <Button
            size="sm"
            onClick={() => digestMutation.mutate()}
            disabled={digestMutation.isLoading || requiresMfa}
          >
            {digestMutation.isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            {digestMutation.isLoading
              ? t('payrollBridge:owner.digestSending')
              : t('payrollBridge:owner.digestCta')}
          </Button>
        </div>
      </div>

      {requiresMfa ? (
        <PayrollMfaGate onVerified={handleMfaVerified} />
      ) : (
        <>
          <Alert className="border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
            <AlertDescription>{t('payrollBridge:owner.disclaimer')}</AlertDescription>
          </Alert>

          {showQueryError ? (
            <Alert
              variant="destructive"
              className="border border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100"
            >
              <AlertDescription>
                {t('payrollBridge:owner.loadError', {
                  defaultValue: 'Unable to load owner summary. {{message}}',
                  message:
                    queryErrorMessage ?? t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          {isLoading ? (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Card key={index} className={metricCardClass}>
                  <CardContent className="space-y-3 py-6">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-5 w-1/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

      {!isLoading && summaryQuery.data ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {highlights.map((item) => (
              <Card key={item.id} className={metricCardClass}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    {item.label}
                  </CardTitle>
                  {item.subLabel ? (
                    <CardDescription className="text-xs text-muted-foreground">
                      {item.subLabel}
                    </CardDescription>
                  ) : null}
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <span className="text-2xl font-semibold text-foreground">{item.value}</span>
                  {item.badge ? (
                    <Badge
                      variant="secondary"
                      className={cn('border px-2 py-0.5 text-xs font-medium', item.badgeClassName)}
                    >
                      {item.badge}
                    </Badge>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border border-border bg-card shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold text-foreground">
                    {t('payrollBridge:owner.alertsTitle')}
                  </CardTitle>
                  <CardDescription>
                    {summaryQuery.data.range.label.replace(/_/g, ' ')}
                    {isRefetching ? (
                      <span className="ml-2 inline-flex items-center text-xs text-muted-foreground">
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        Updating…
                      </span>
                    ) : null}
                  </CardDescription>
                </div>
                <BarChart3 className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
              </CardHeader>
              <CardContent>
                <AlertsList
                  alerts={summaryQuery.data.alerts}
                  emptyText={t('payrollBridge:owner.alertsEmpty')}
                  reviewLabel={t('common:actions.review', { defaultValue: 'Review' })}
                  resolveHref={resolveAlertHref}
                />
              </CardContent>
            </Card>

            <Card className="border border-border bg-card shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-foreground">
                  {t('payrollBridge:owner.highlightsTitle')}
                </CardTitle>
                <CardDescription>{summaryQuery.data.range.label.replace(/_/g, ' ')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  {summaryQuery.data.alerts.length > 0 ? (
                    summaryQuery.data.alerts.map((alert) => (
                      <li key={`digest-${alert.id}`} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                        <span>{alert.title} - {alert.message}</span>
                      </li>
                    ))
                  ) : (
                    highlights.slice(0, 4).map((highlight) => (
                      <li key={`highlight-${highlight.id}`} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-muted-foreground/40 dark:bg-muted-foreground/70" />
                        <span>{highlight.label}: {highlight.value}</span>
                      </li>
                    ))
                  )}
                </ul>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}

      {!isLoading && summaryQuery.data ? (
        <Card className="border border-border bg-card shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-foreground">
              {t('payrollBridge:owner.actionsTitle')}
            </CardTitle>
            <CardDescription>
              {summaryQuery.data.range.label.replace(/_/g, ' ')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <a href={tenantPath('payroll/onboarding')}>
                  {t('payrollBridge:owner.openOnboarding')}
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href={tenantPath('payroll/tips')}>
                  {t('payrollBridge:owner.openTipPooling', { defaultValue: 'Manage tip pools' })}
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href={`${tenantPath('payroll/onboarding')}#exports`}>
                  {t('payrollBridge:owner.openExports')}
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href={`${tenantPath('payroll/onboarding')}#exceptions`}>
                  {t('payrollBridge:owner.viewExceptions')}
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
        </>
      )}
    </div>
  );
};

export default PayrollOwnerDashboard;
